/**
 * Operating-Calendar API service for the Whakahaere Safety Management System.
 *
 * Backs the ADR-126 Decision 3 site operating-calendar config UI:
 * recurring operating hours, dated closures/exceptions, and scheduled
 * drill/training/field-exercise events, attached at the org (district) or
 * site (school) tier with org→site inheritance.
 *
 * Uses the typed `@tiaki/http-client` surface per ADR-113. The backend
 * `operating` app (`SiteOperatingCalendar`, `OperatingHours`,
 * `OperatingException`, `ScheduledSiteEvent` + resolver + REST layer) is in
 * the generated OpenAPI schema, so the resource shapes and call sites come
 * straight off `components['schemas']` and `client.GET/POST/PATCH/DELETE` —
 * no `client.ky` escape hatch and no hand-written request/response shapes
 * to drift.
 *
 * Contract note: these list endpoints are NOT DRF-paginated — the `operating`
 * viewsets declare no `pagination_class`, so `list` returns a bare array, not
 * a `{count,next,previous,results}` envelope.
 */

import type { components } from '@tiaki/http-client/types';
import { client } from '../lib/http-client';

function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error !== undefined) {
    throw result.error;
  }
  return result.data as T;
}

// ---------------------------------------------------------------------------
// Enums — re-exported from the generated schema (mirror
// server.apps.operating.models). The label maps stay app-owned (display copy).
// ---------------------------------------------------------------------------

/** ISO weekday, matching `datetime.date.weekday()` (Mon=0 … Sun=6). */
export type Weekday = components['schemas']['WeekdayEnum'];

export const WEEKDAY_LABELS: Record<Weekday, string> = {
  0: 'Monday',
  1: 'Tuesday',
  2: 'Wednesday',
  3: 'Thursday',
  4: 'Friday',
  5: 'Saturday',
  6: 'Sunday',
};

/** Resolved, vertical-agnostic site state (the per-tenant overlay renames it). */
export type OperatingState = 'operating' | 'not_operating';

/** How a dated `OperatingException` modifies that calendar date. */
export type ExceptionKind = components['schemas']['OperatingExceptionKindEnum'];

export const EXCEPTION_KIND_LABELS: Record<ExceptionKind, string> = {
  closed: 'Closed all day',
  open: 'Open all day',
  custom_hours: 'Custom hours',
};

/** Category of a scheduled site event on the operating timeline. */
export type SiteEventType =
  components['schemas']['ScheduledSiteEventEventTypeEnum'];

export const SITE_EVENT_TYPE_LABELS: Record<SiteEventType, string> = {
  drill: 'Drill',
  training: 'Training',
  field_exercise: 'Field exercise',
  other: 'Other',
};

// ---------------------------------------------------------------------------
// Resource shapes — sourced from the generated OpenAPI components.
// ---------------------------------------------------------------------------

/** One recurring (weekday, open_time, close_time) window on a calendar. */
export type OperatingHours = components['schemas']['OperatingHours'];

/** A dated override that wins over the weekly pattern for a single date. */
export type OperatingException = components['schemas']['OperatingException'];

/** A typed scheduled site event (drill / training / field exercise). */
export type ScheduledSiteEvent = components['schemas']['ScheduledSiteEvent'];

/**
 * A site's (or org's default) operating calendar. Exactly one of `school` /
 * `district` is set: `school` => site-level (overrides the org default for
 * that site); `district` => org-level default inherited by sites without
 * their own.
 */
export type OperatingCalendar = components['schemas']['SiteOperatingCalendar'];

/** The resolved-state preview surfaced by the backend resolver (read-only). */
export type ResolvedState = components['schemas']['ResolvedState'];

export interface OperatingCalendarFilters {
  /** Filter to a single org's default calendar. */
  district?: number;
  /** Filter to a single site's calendar. */
  school?: number;
  ordering?: string;
}

// ---------------------------------------------------------------------------
// Calendar CRUD
// ---------------------------------------------------------------------------

export type OperatingCalendarWriteInput =
  components['schemas']['SiteOperatingCalendarRequest'];

/**
 * List operating calendars. NB: unpaginated — returns a bare array
 * (the `operating` viewsets declare no `pagination_class`).
 */
export async function getOperatingCalendars(
  filters: OperatingCalendarFilters = {},
): Promise<OperatingCalendar[]> {
  const result = await client.GET('/api/v2/operating/calendars/', {
    params: { query: filters },
  });
  return unwrap(result);
}

export async function getOperatingCalendarById(
  id: number,
): Promise<OperatingCalendar> {
  const result = await client.GET('/api/v2/operating/calendars/{id}/', {
    params: { path: { id } },
  });
  return unwrap(result);
}

/**
 * Resolve the *effective* calendar for a site, honoring org→site inheritance.
 * Returns the calendar that governs the site (site override > org default), or
 * `null` when neither the site nor its org has one (backend 404).
 */
export async function getEffectiveCalendarForSchool(
  schoolId: number,
): Promise<OperatingCalendar | null> {
  const result = await client.GET('/api/v2/operating/calendars/effective/', {
    params: { query: { school: schoolId } },
  });
  if (result.response.status === 404) return null;
  return unwrap(result);
}

/** Read-only resolved-state preview for a site at the current instant. */
export async function resolveSiteState(
  schoolId: number,
): Promise<ResolvedState | null> {
  const result = await client.GET('/api/v2/operating/calendars/resolve/', {
    params: { query: { school: schoolId } },
  });
  if (result.response.status === 404) return null;
  return unwrap(result);
}

export async function createOperatingCalendar(
  data: OperatingCalendarWriteInput,
): Promise<OperatingCalendar> {
  const result = await client.POST('/api/v2/operating/calendars/', {
    body: data,
  });
  return unwrap(result);
}

export async function updateOperatingCalendar(
  id: number,
  data: Partial<OperatingCalendarWriteInput>,
): Promise<OperatingCalendar> {
  const result = await client.PATCH('/api/v2/operating/calendars/{id}/', {
    params: { path: { id } },
    body: data,
  });
  return unwrap(result);
}

export async function deleteOperatingCalendar(id: number): Promise<void> {
  const result = await client.DELETE('/api/v2/operating/calendars/{id}/', {
    params: { path: { id } },
  });
  unwrap(result);
}

// ---------------------------------------------------------------------------
// Operating-hours CRUD (child of a calendar)
// ---------------------------------------------------------------------------

export type OperatingHoursWriteInput =
  components['schemas']['OperatingHoursRequest'];

export async function getOperatingHours(
  calendarId: number,
): Promise<OperatingHours[]> {
  const result = await client.GET('/api/v2/operating/hours/', {
    params: { query: { calendar: calendarId } },
  });
  return unwrap(result);
}

export async function createOperatingHours(
  data: OperatingHoursWriteInput,
): Promise<OperatingHours> {
  const result = await client.POST('/api/v2/operating/hours/', { body: data });
  return unwrap(result);
}

export async function updateOperatingHours(
  id: number,
  data: Partial<OperatingHoursWriteInput>,
): Promise<OperatingHours> {
  const result = await client.PATCH('/api/v2/operating/hours/{id}/', {
    params: { path: { id } },
    body: data,
  });
  return unwrap(result);
}

export async function deleteOperatingHours(id: number): Promise<void> {
  const result = await client.DELETE('/api/v2/operating/hours/{id}/', {
    params: { path: { id } },
  });
  unwrap(result);
}

// ---------------------------------------------------------------------------
// Operating-exception CRUD (child of a calendar)
// ---------------------------------------------------------------------------

export type OperatingExceptionWriteInput =
  components['schemas']['OperatingExceptionRequest'];

export async function getOperatingExceptions(
  calendarId: number,
): Promise<OperatingException[]> {
  const result = await client.GET('/api/v2/operating/exceptions/', {
    params: { query: { calendar: calendarId } },
  });
  return unwrap(result);
}

export async function createOperatingException(
  data: OperatingExceptionWriteInput,
): Promise<OperatingException> {
  const result = await client.POST('/api/v2/operating/exceptions/', {
    body: data,
  });
  return unwrap(result);
}

export async function updateOperatingException(
  id: number,
  data: Partial<OperatingExceptionWriteInput>,
): Promise<OperatingException> {
  const result = await client.PATCH('/api/v2/operating/exceptions/{id}/', {
    params: { path: { id } },
    body: data,
  });
  return unwrap(result);
}

export async function deleteOperatingException(id: number): Promise<void> {
  const result = await client.DELETE('/api/v2/operating/exceptions/{id}/', {
    params: { path: { id } },
  });
  unwrap(result);
}

// ---------------------------------------------------------------------------
// Scheduled-site-event CRUD (child of a calendar)
// ---------------------------------------------------------------------------

export type ScheduledSiteEventWriteInput =
  components['schemas']['ScheduledSiteEventRequest'];

export async function getScheduledSiteEvents(
  calendarId: number,
): Promise<ScheduledSiteEvent[]> {
  const result = await client.GET('/api/v2/operating/events/', {
    params: { query: { calendar: calendarId } },
  });
  return unwrap(result);
}

export async function createScheduledSiteEvent(
  data: ScheduledSiteEventWriteInput,
): Promise<ScheduledSiteEvent> {
  const result = await client.POST('/api/v2/operating/events/', { body: data });
  return unwrap(result);
}

export async function updateScheduledSiteEvent(
  id: number,
  data: Partial<ScheduledSiteEventWriteInput>,
): Promise<ScheduledSiteEvent> {
  const result = await client.PATCH('/api/v2/operating/events/{id}/', {
    params: { path: { id } },
    body: data,
  });
  return unwrap(result);
}

export async function deleteScheduledSiteEvent(id: number): Promise<void> {
  const result = await client.DELETE('/api/v2/operating/events/{id}/', {
    params: { path: { id } },
  });
  unwrap(result);
}
